import React, { useEffect, useMemo } from 'react';
import { DeepDiveTabsGraphDisplay } from 'apps/sigma2-ui/src/components/DeepDiveTabsGraphDisplay';
import { getControlVariableNameFromRootCauseOutputs } from 'libs/end-to-end/src/root-cause-analysis';
import { getDeepDiveAnalysisTypeFromRootCauseOutputs } from 'libs/end-to-end/src/root-cause-analysis';
import { RootCauseGroupResult } from 'libs/end-to-end/src/root-cause-analysis';
import { extractRootCauseTextOutputs } from 'libs/end-to-end/src/root-cause-analysis';
import { DeepDiveEmptyExplanatoryFactorsSubTabState } from 'apps/sigma2-ui/src/focus-area/deep-dive/DeepDiveEmptyLoadingAndErrorStates';
import { Nullable } from 'libs/end-to-end/src/ui-types';
import { MyFocusAreaDefinition } from 'libs/end-to-end/src/my-focus-area-definition';
import { MyMetricDefinition } from 'libs/end-to-end/src/my-metric-definition';
import { DimensionDefinition } from 'libs/end-to-end/src/types/databases/dimensions/dimension-definition';
import { LevelDefinition } from 'libs/end-to-end/src/types/databases/dimensions/level-definition';
import { useAnalytics } from 'apps/sigma2-ui/src/lib/hooks/useAnalytics';
import { getBarChartCategories } from 'libs/end-to-end/src/oaxaca-bar-chart';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { CalibrationFraming } from 'libs/end-to-end/src/calibration-framing';

export const DeepDiveExplanatoryFactorsTab: React.FC<{
  focusAreaDefinition: MyFocusAreaDefinition;
  metricDefinition: Nullable<MyMetricDefinition>;
  dimensionDefinition: Nullable<DimensionDefinition>;
  treatmentGroupLevelDefinition: Nullable<LevelDefinition>;
  referenceGroupLevelDefinition: Nullable<LevelDefinition>;
  groupResult: Nullable<RootCauseGroupResult>;
  graphRef: React.RefObject<HTMLInputElement>;
  carouselSwitcher: React.ReactNode;
  performanceMetricDefinition: Nullable<MyMetricDefinition>;
  calibrationFraming: CalibrationFraming;
  // eslint-disable-next-line complexity
}> = ({
  focusAreaDefinition,
  metricDefinition,
  dimensionDefinition,
  treatmentGroupLevelDefinition,
  referenceGroupLevelDefinition,
  groupResult,
  carouselSwitcher,
  graphRef,
  performanceMetricDefinition,
  calibrationFraming,
}) => {
  const analytics = useAnalytics();
  const language = useLanguage();

  useEffect(() => {
    analytics.trackEvent({
      eventName: 'Deep Dive Section Viewed',
      eventProperties: {
        selectedTab: 'Explanatory Factors',
      },
    });
  }, [analytics]);

  const resultCategories = useMemo(() => {
    return getBarChartCategories({ groupResult });
  }, [groupResult]);

  const rootCauseOutputs = useMemo(() => {
    return extractRootCauseTextOutputs(groupResult);
  }, [groupResult]);

  const controlVariableName = useMemo(() => {
    return getControlVariableNameFromRootCauseOutputs(rootCauseOutputs);
  }, [rootCauseOutputs]);

  const analysisType = useMemo(() => {
    return getDeepDiveAnalysisTypeFromRootCauseOutputs({
      rootCauseOutputs,
    });
  }, [rootCauseOutputs]);

  if (
    !metricDefinition ||
    !dimensionDefinition ||
    !treatmentGroupLevelDefinition ||
    !referenceGroupLevelDefinition
  ) {
    return <></>;
  }

  if (!groupResult) {
    return (
      <DeepDiveEmptyExplanatoryFactorsSubTabState
        treatmentLevelDefinition={treatmentGroupLevelDefinition}
        referenceLevelDefinition={referenceGroupLevelDefinition}
        metricDefinition={metricDefinition}
      />
    );
  }

  return (
    <DeepDiveTabsGraphDisplay
      calibrationFraming={calibrationFraming}
      metricDefinition={metricDefinition}
      treatmentGroupLevelDefinition={treatmentGroupLevelDefinition}
      referenceGroupLevelDefinition={referenceGroupLevelDefinition}
      dimensionDefinition={dimensionDefinition}
      carouselSwitcher={carouselSwitcher}
      graphRef={graphRef}
      focusAreaDefinition={focusAreaDefinition}
      groupResult={groupResult}
      performanceMetricDefinition={performanceMetricDefinition}
      graphType={'root-cause'}
      headlineTranslatedContent={language.headlineForExplanatoryFactors({
        controlVariableName: controlVariableName,
      })}
      descriptionTranslatedContent={language.explanationForExplanatoryFactors({
        metricDefinition,
        dimensionDefinition,
        treatmentLevelDefinition: treatmentGroupLevelDefinition,
        referenceLevelDefinition: referenceGroupLevelDefinition,
        controlVariableName: controlVariableName,
        analysisType,
        plotType: groupResult.plot_type,
        categoriesName: resultCategories,
      })}
    />
  );
};
